import React, {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  Experiment,
  ExperimentVariant,
  ExperimentState,
  CallState,
} from "redmond";
import { fetchActiveExperiments } from "../api/v0/experiments/fetchExperiments";

// Active Experiments
// export const EXAMPLE = "c1-example";

export const CUSTOMER_PROFILE_EXPERIMENT = "c1-marketplace-customer-profile";
export const CREDIT_OFFER_STACKING_V1 =
  "c1-marketplace-credit-offer-stacking-v1";
export const TRAVEL_CREDIT_HISTORY_EXPERIMENT =
  "c1-marketplace-credit-transaction-history";
export const ANNUAL_TRAVEL_CREDITS = "c1-marketplace-annual-travel-credits";
export const TRAVEL_SALE = "c1-marketplace-travel-sale";
export const CORP_DEBUGGING_PANEL = "corp-dynamic-limits";
export const TREES_MODAL_EXPERIMENT = "c1-marketplace-trees";
export const HOTELS_CALIFORNIA_BILL_644_EXPERIMENT =
  "c1-marketplace-HotelsCaliforniaBill644";
export const CACHE_HOTEL_TOKEN = "c1-marketplace-cache-hotel-token";
export const PACKAGES_FORCED_PAX = "c1-marketplace-packages-forced-pax";
export const PACKAGES_ORIGIN_MULTIPLE_AIRPORT =
  "c1-marketplace-package-origin-multiple-airport";
export const PACKAGES_CHECKOUT_OPTIMIZATIONS_V1 =
  "c1-marketplace-packages-checkout-optimizations-v1";
export const PACKAGES_SHOPPING_API_V2 = "packages-shopping-api-v2";
export const CORP_FTB_OFFER_ADDITIONAL_PLACEMENTS =
  "corp-ftb-offer-additional-placements";
export const PACKAGES_DISCOVERY_DRIVEN_SEARCH =
  "c1-packages-discovery-driven-search";
export const PACKAGES_LIST_OPTIMIZATIONS_V1 =
  "c1-marketplace-packages-list-optimizations-v1";
export const XMOD_V1_EXPERIMENT = "c1-marketplace-xmod-v1";
export const EAN_CERTIFICATION = "c1-marketplace-ean-certification-packages";
export const HOTELS_CANCELLATION_POLICY_EXPERIMENT =
  "c1-hotels-supply-cancellation-policy";
export const PACKAGES_PRICING_DISPLAY = "c1-packages-pricing-display";
export const PACKAGES_TAG_EXCLUSIVE_RATES = "c1-packages-tag-exclusive-rates";
export const TRAVEL_APP_GTM_EXPERIMENT = "c1-portex-app-gtm";
export const PROJECTS_AND_CATEGORIES_FLOW = "c1-projects-and-categories-flow";

// Default variants
export const CONTROL = "control";
export const AVAILABLE = "available";
export const PRODUCTION = "production";
export const PRODUCTION_1 = "Production1";
export const PRODUCTION_2 = "Production2";

// Variants for c1-marketplace-customer-profile
export const DEFAULT_OFF = "default-off";
export const DEFAULT_ON = "default-on";
export const CUSTOMER_PROFILE_VARIANTS = [
  CONTROL,
  DEFAULT_OFF,
  DEFAULT_ON,
] as const;

// Variants for c1-marketplace-travel-sale
export const TRAVEL_SALE_LEAD_UP = "lead-up";
export const TRAVEL_SALE_ACTIVE = "active";
export const TRAVEL_SALE_VARIANTS = [
  CONTROL,
  TRAVEL_SALE_LEAD_UP,
  TRAVEL_SALE_ACTIVE,
] as const;
export type TravelSalePropVariantType =
  | typeof CONTROL
  | typeof TRAVEL_SALE_LEAD_UP
  | typeof TRAVEL_SALE_ACTIVE;

export const CORE_HOMEPAGE = "c1-homepage-redesign";

// Variants for c1-packages-discovery-driven-search
export const LOCATION_BASED_DISCOVERY = "LocationBasedDiscovery";
export const ACTION_BASED_DISCOVERY = "ActionBasedDiscovery";
export const PACKAGES_DISCOVERY_DRIVEN_SEARCH_VARIANTS = [
  PRODUCTION_1,
  PRODUCTION_2,
  CONTROL,
  LOCATION_BASED_DISCOVERY,
  ACTION_BASED_DISCOVERY,
] as const;

// Variants for c1-packages-pricing-display
export const CHANGE_PER_TRAVELER = "ChangePerTraveler";
export const TOTAL_PER_TRAVELER = "TotalPerTraveler";
export const PACKAGES_PRICING_DISPLAY_VARIANTS = [
  PRODUCTION_1,
  PRODUCTION_2,
  CONTROL,
  CHANGE_PER_TRAVELER,
  TOTAL_PER_TRAVELER,
] as const;

const defaultInitializer = (): ExperimentStateWithCallState => {
  return {
    experiments: [],
    trackingProperties: undefined,
    callState: CallState.NotCalled,
  };
};

export const ExperimentsContext = createContext<
  ExperimentStateWithCallState | undefined
>(undefined);

// readonly (together with const restriction below) ensures typeof supportedVariants[number]
// can be recognized by transpiler as an array of constants
// [string, ...string[]] ensures array is not empty.
export function getExperimentVariantCustomVariants<
  T extends readonly [string, ...string[]],
>(
  experiments: Array<Experiment>,
  experimentId: string,
  // List of supported variants. If the variant retreived is not recognized by the code, the code will fall back to the first variant.
  // Note the input list needs to be declared as const e.g. const DEFAULT_VARIANTS = [CONTROL, AVAILABLE] as const;
  supportedVariants: T,
): (typeof supportedVariants)[number] {
  const experiment = experiments?.find((exp) => exp.id === experimentId);

  if (experiment) {
    if (supportedVariants.includes(experiment.variant)) {
      return experiment.variant;
    } else {
      return supportedVariants[0];
    }
  } else {
    return supportedVariants[0];
  }
}

export function getExperimentVariant(
  experiments: Array<Experiment> = [],
  experimentId: String,
  defaultExperimentVariant: ExperimentVariant = ExperimentVariant.CONTROL,
): ExperimentVariant {
  const experiment = experiments?.find((exp) => {
    if (exp.id === experimentId) {
      return exp;
    }
    return null;
  });

  return (experiment?.variant as ExperimentVariant) ?? defaultExperimentVariant;
}

interface ExperimentStateWithCallState extends ExperimentState {
  callState: CallState;
}

export function useExperiments(): ExperimentStateWithCallState {
  const ctx = useContext(ExperimentsContext);
  if (!ctx) throw new Error(`must be used within a ExperimentsProvider`);
  return ctx;
}

export function useIsPackagesShoppingApiV2Enabled(): boolean {
  const { experiments } = useExperiments();
  return getExperimentVariant(experiments, PACKAGES_SHOPPING_API_V2) === AVAILABLE;
}

export function addTrackingProperties(
  trackingProperties: Map<string, string> | undefined,
  properties?: any,
): unknown {
  if (!properties) {
    properties = {};
  }
  if (trackingProperties) {
    properties["experiments"] = Object.keys(trackingProperties).map(
      (ex) => `${ex}_${trackingProperties![ex]}`,
    );
  }
  return properties;
}

const ExperimentsProvider: FC<
  PropsWithChildren<{ initState?: ExperimentStateWithCallState }>
> = ({ initState = defaultInitializer(), children }) => {
  // TODO: use tracking reducers
  const [state, setState] = useState(initState);

  useEffect(() => {
    if (!initState?.experiments.length) {
      const fetchExperiments = async () => {
        setState({ ...state, callState: CallState.InProcess });
        await fetchActiveExperiments().then((result) => {
          setState({
            ...(result as ExperimentState),
            callState: CallState.Success,
          });
        });
      };
      fetchExperiments();
    }
  }, []);

  return (
    <ExperimentsContext.Provider value={state}>
      {children}
    </ExperimentsContext.Provider>
  );
};

export default ExperimentsProvider;
